import React, { useState, useMemo, useCallback } from 'react';
import { ContractWizardState, ContractEvent, ContractProperty, ContractPropertyTreeNode, TransformationConfig, TransformationType } from '../../types/contract';
import { Search, X, Sparkles, Plus, Check } from 'lucide-react'; // Added Plus icon for Add Property
import { LoadingSpinner } from '../LoadingSpinner';
import ContractPropertyTableList from './ContractPropertyTableList';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import clsx from 'clsx';
import ContractEventDetailsEditor from './ContractEventDetailsEditor'; // Import the new component

interface NormalizationPropertiesPanelProps {
  state: ContractWizardState;
  updateState: (updates: Partial<ContractWizardState>) => void;
  selectedEvent: ContractEvent | null;
  availableMappings: { name: string; path: string; type: string; certaintyScore?: number }[];
  isLoadingMappings: boolean;
  handleAIMappingSuggestion: (propertyId: string) => Promise<void>;
  handleAIMappingBatch: () => Promise<void>;
  handleCancelAIMapping?: () => void;
  handleAutoMapExactMatches: () => void;
  handleRemoveMapping: (propertyId: string) => void;
  isProcessingAI: boolean;
  className?: string;
  // New handlers for property management
  handleAddProperty: (eventId: string, parentProperty: ContractProperty | null) => void;
  handleUpdateProperty: (propertyId: string, updates: Partial<ContractProperty>) => void;
  handleDeleteProperty: (propertyId: string, deleteFromAllEvents?: boolean) => void;
  isEventNameEditing: boolean;
  setIsEventNameEditing: (isEditing: boolean) => void;
  isEventCodeNameEditing: boolean;
  setIsEventCodeNameEditing: (isEditing: boolean) => void;
  eventCodeNameError: string | null;
  eventNameInputRef: React.RefObject<HTMLInputElement>;
  eventCodeNameInputRef: React.RefObject<HTMLInputElement>;
  handleEventChange: (eventId: string, field: 'name' | 'codeName', value: string) => void;
}

const NormalizationPropertiesPanel: React.FC<NormalizationPropertiesPanelProps> = ({
  state,
  updateState, // Added updateState here
  selectedEvent,
  availableMappings,
  isLoadingMappings,
  handleAIMappingBatch,
  handleCancelAIMapping,
  handleAutoMapExactMatches,
  isProcessingAI,
  className,
  handleAddProperty,
  handleUpdateProperty,
  handleDeleteProperty,
  isEventNameEditing,
  setIsEventNameEditing,
  isEventCodeNameEditing,
  setIsEventCodeNameEditing,
  eventCodeNameError,
  eventNameInputRef,
  eventCodeNameInputRef,
  handleEventChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeEditingPropertyId, setActiveEditingPropertyId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [propertyToDeleteId, setPropertyToDeleteId] = useState<string | null>(null);

  // Properties to display: filter from allContractProperties based on selected event
  const propertiesToDisplay = useMemo(() => {
    if (!selectedEvent) {
      // If no event is selected, show all global properties or all properties if no scope is defined
      return state.allContractProperties.filter(p => p.scope === 'global' || p.scope === undefined);
    }
    // Filter properties that are associated with the selected event
    const eventPropertyPaths = new Set(selectedEvent.properties.map(p => p.path));
    const associatedProperties = state.allContractProperties.filter(p => {
      // Include the property itself if its path is in the event's properties
      if (eventPropertyPaths.has(p.path)) return true;
      // Include the property if any of its ancestors are in the event's properties
      // This ensures that when a parent is associated, its children are also considered for display
      return p.parentPaths?.some(parentPathSegment => eventPropertyPaths.has(p.path.substring(0, p.path.indexOf(parentPathSegment) + parentPathSegment.length))) || false;
    });

    // Ensure that if a child is associated, its parents are also included in the display list
    const finalPropertiesToDisplay = new Set<ContractPropertyTreeNode>();
    associatedProperties.forEach(prop => {
      finalPropertiesToDisplay.add(prop);
      let currentPathSegments = prop.path.split('.');
      while (currentPathSegments.length > 0) {
        currentPathSegments.pop();
        if (currentPathSegments.length > 0) {
          const parentPath = currentPathSegments.join('.');
          const parentProp = state.allContractProperties.find(p => p.path === parentPath);
          if (parentProp) {
            finalPropertiesToDisplay.add(parentProp);
          }
        }
      }
    });

    return Array.from(finalPropertiesToDisplay);
  }, [selectedEvent, state.allContractProperties]);

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    if (!searchTerm) return propertiesToDisplay;

    return propertiesToDisplay.filter(property =>
      property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (property.friendlyName && property.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [propertiesToDisplay, searchTerm]);

  // Handle transformation type change
  const handleTransformationTypeChange = useCallback((propertyId: string, type: TransformationType) => {
    const normalizedProperty = state.normalizedProperties[propertyId];

    let transformationConfig: TransformationConfig = normalizedProperty?.transformationConfig || {};

    switch (type) {
      case 'mapping':
        transformationConfig = {
          ...transformationConfig,
          mappingType: transformationConfig.mappingType || 'direct',
          sourceFields: transformationConfig.sourceFields || [],
          separator: transformationConfig.separator || '.',
          regexPattern: transformationConfig.regexPattern ?? '',
        };
        break;
      case 'enrichment':
        transformationConfig = {
          ...transformationConfig,
          enrichmentType: transformationConfig.enrichmentType || 'direct',
          value: transformationConfig.value || '',
        };
        break;
      case 'lua':
        transformationConfig = {
          ...transformationConfig,
          script: transformationConfig.script || 'function transform(input)\n  -- Your transformation logic here\n  return input\nend',
        };
        break;
      case 'spreading':
        transformationConfig = {
          ...transformationConfig,
          targetPrefix: transformationConfig.targetPrefix || '',
          includeFields: transformationConfig.includeFields || [],
          excludeFields: transformationConfig.excludeFields || [],
        };
        break;
    }

    handleUpdateProperty(propertyId, {
      transformationType: type,
      transformationConfig
    });
  }, [handleUpdateProperty, state.normalizedProperties]);

  // Handle transformation config update
  const handleTransformationConfigUpdate = useCallback((propertyId: string, configUpdates: Partial<TransformationConfig>) => {
    handleUpdateProperty(propertyId, {
      transformationConfig: {
        ...state.normalizedProperties[propertyId]?.transformationConfig,
        ...configUpdates
      } as TransformationConfig
    });
  }, [handleUpdateProperty, state.normalizedProperties]);

  // Handle property event association change
  const handlePropertyEventAssociationChange = useCallback((propertyId: string, eventId: string, isAssociated: boolean) => {
    const updatedAllContractProperties = state.allContractProperties.map((prop: ContractPropertyTreeNode) => {
      if (prop.id === propertyId) {
        const currentAssociations = new Set(prop.eventAssociations || []);
        if (isAssociated) {
          currentAssociations.add(eventId);
        } else {
          currentAssociations.delete(eventId);
        }
        return { ...prop, eventAssociations: Array.from(currentAssociations) };
      }
      return prop;
    });
    updateState({ allContractProperties: updatedAllContractProperties });
  }, [updateState, state.allContractProperties]);

  // Memoized and sorted available mappings for the Listbox
  const sortedAvailableMappings = useMemo(() => {
    const mappings = [...availableMappings];
    mappings.sort((a, b) => {
      const scoreA = a.certaintyScore ?? -1;
      const scoreB = b.certaintyScore ?? -1;

      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }
      return a.path.localeCompare(b.path);
    });
    return mappings;
  }, [availableMappings]);

  // Handle property deletion confirmation
  const handleDeletePropertyClick = useCallback((propertyId: string) => {
    setPropertyToDeleteId(propertyId);
    setShowConfirmDeleteDialog(true);
  }, []);

  const confirmDeleteProperty = useCallback(() => {
    if (propertyToDeleteId) {
      handleDeleteProperty(propertyToDeleteId);
      setActiveEditingPropertyId(null); // Close edit form if open
    }
    setShowConfirmDeleteDialog(false);
    setPropertyToDeleteId(null);
  }, [propertyToDeleteId, handleDeleteProperty]);

  const cancelDeleteProperty = useCallback(() => {
    setShowConfirmDeleteDialog(false);
    setPropertyToDeleteId(null);
  }, []);

  // Transform ContractEvent[] to EventSchema[] for the dropdown
  const allEventsAsSchema = useMemo(() => {
    return state.events.map(event => ({
      id: event.id,
      name: event.name,
      codeName: event.codeName,
      description: event.description || '', // Ensure description is a string
      parameterFullPaths: event.properties.map(prop => prop.path),
    }));
  }, [state.events]);

  return (
    <div className={clsx("w-full max-w-[1800px] mx-auto flex flex-col", className)}>
      {selectedEvent ? (
        <div className="p-6 mb-6">
          {/* Event Details Section */}
          <ContractEventDetailsEditor
            selectedEvent={selectedEvent}
            isEditable={true} // Always editable in this context
            handleEventChange={handleEventChange}
            isEventNameEditing={isEventNameEditing}
            setIsEventNameEditing={setIsEventNameEditing}
            isEventCodeNameEditing={isEventCodeNameEditing}
            setIsEventCodeNameEditing={setIsEventCodeNameEditing}
            eventCodeNameError={eventCodeNameError}
            eventNameInputRef={eventNameInputRef}
            eventCodeNameInputRef={eventCodeNameInputRef}
          />

          {/* Search Input and Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
            <div className="relative flex-grow w-full sm:w-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search properties by name or path"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                </button>
              )}
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleAutoMapExactMatches}
                disabled={isLoadingMappings || propertiesToDisplay.length === 0 || Object.keys(state.normalizedProperties).length === propertiesToDisplay.length}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Check className="mr-2 h-5 w-5" />
                Auto-map
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isProcessingAI) {
                    setIsCancelling(true);
                    if (handleCancelAIMapping) {
                      handleCancelAIMapping();
                      setTimeout(() => setIsCancelling(false), 1000);
                    }
                  } else {
                    handleAIMappingBatch();
                  }
                }}
                disabled={(isProcessingAI && !isCancelling) || isLoadingMappings || propertiesToDisplay.length === 0 || Object.keys(state.normalizedProperties).length === propertiesToDisplay.length}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isProcessingAI ? (
                  isCancelling ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2 text-white" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Matching...
                    </>
                  )
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    AI Match
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => selectedEvent && handleAddProperty(selectedEvent.id, null)}
                disabled={!selectedEvent}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Property
              </button>
            </div>
          </div>

          {/* Properties Display */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {isLoadingMappings ? (
              <div className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading available mappings...
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {selectedEvent ?
                  `No properties found for event "${selectedEvent.name}" or matching search criteria.` :
                  'No properties found or matching search criteria.'
                }
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                <ContractPropertyTableList
                  properties={filteredProperties}
                  isEditable={true}
                  handleUpdateProperty={handleUpdateProperty}
                  handleDeleteProperty={handleDeletePropertyClick}
                  activeEditingPropertyId={activeEditingPropertyId}
                  setActiveEditingPropertyId={setActiveEditingPropertyId}
                  availableMappings={sortedAvailableMappings}
                  handleTransformationConfigUpdate={handleTransformationConfigUpdate}
                  handleTransformationTypeChange={handleTransformationTypeChange}
                  handleAddProperty={(parentProperty) => selectedEvent && handleAddProperty(selectedEvent.id, parentProperty)}
                  handlePropertyEventAssociationChange={handlePropertyEventAssociationChange}
                  allEvents={allEventsAsSchema}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <p className="text-lg font-semibold mb-2">No event selected</p>
          <p className="text-sm">Please select an event from the left panel to normalize its data.</p>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {showConfirmDeleteDialog && (
        <ConfirmDialog
          onClose={cancelDeleteProperty}
          onConfirm={confirmDeleteProperty}
          title="Delete Property"
          message="Are you sure you want to delete this property? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};

export default NormalizationPropertiesPanel;
